import React, { useCallback, useEffect, useState } from 'react';

import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { Alert, Box, Button, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { useUnit } from 'effector-react';
import { useSnackbar } from 'notistack';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { Droppable } from './Droppable';
import { Faq$, faqApi } from '../../../../effector/faq';
import { Question } from '../../../../types/faq';
import { getMessageFromFailedRequest } from '../../../../utils';

export enum ItemTypes {
  ITEM = 'ITEM',
}

export const Mapping: React.FC = () => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { categories, questions, categoryQuestions } = useUnit(Faq$);

  const { enqueueSnackbar } = useSnackbar();

  const [columns, setColumns] = useState<{ category: Question[]; questions: Question[] }>({
    category: [],
    questions: [],
  });

  const handleCategoryClick = useCallback((category: string) => {
    setSelectedCategory(category);
  }, []);

  const moveItem = useCallback(
    (
      dragColumnIndex: 'category' | 'questions',
      dragIndex: number,
      hoverColumnIndex: 'category' | 'questions',
      hoverIndex: number,
    ) => {
      const sourceColumn = columns[dragColumnIndex];
      const destinationColumn = columns[hoverColumnIndex];

      const newSourceItems = Array.from(sourceColumn);
      const newDestinationItems = Array.from(destinationColumn);

      newSourceItems.splice(dragIndex, 1);

      const isDifferentColumn = dragColumnIndex !== hoverColumnIndex;

      if (isDifferentColumn) {
        newDestinationItems.splice(hoverIndex, 0, sourceColumn[dragIndex]);
      } else {
        [newDestinationItems[hoverIndex], newDestinationItems[dragIndex]] = [
          newDestinationItems[dragIndex],
          newDestinationItems[hoverIndex],
        ];
      }

      setColumns((state) => ({
        ...state,
        [dragColumnIndex]: newSourceItems,
        [hoverColumnIndex]: newDestinationItems,
      }));
    },
    [columns],
  );

  const handleError = useCallback((e: Error) => {
    const message = getMessageFromFailedRequest(e);
    setError(message);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!selectedCategory) return;

    setLoading(true);
    setError('');
    const questionIds = columns.category.map(({ id }) => id);
    faqApi
      .editCategoryQuestions({ categoryId: selectedCategory, questionIds })
      .then(() => {
        enqueueSnackbar('Связи обновлены');
      })
      .catch(handleError)
      .finally(() => {
        setLoading(false);
      });
  }, [selectedCategory, columns, handleError, enqueueSnackbar]);

  useEffect(() => {
    faqApi.getCategories('');
    faqApi.getQuestions('');
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      faqApi.getCategoryQuestions(selectedCategory);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (categories?.length && !selectedCategory) {
      setSelectedCategory(categories[0].id);
    }
  }, [categories, selectedCategory]);

  useEffect(() => {
    const categoryQuestionIds = categoryQuestions?.map(({ id }) => id);

    setColumns({
      category: categoryQuestions,
      questions: questions.filter(({ id }) => !categoryQuestionIds.includes(id)),
    });
  }, [questions, categoryQuestions]);

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 2,
        }}
      >
        <Paper variant="outlined" sx={{ p: 1.5, minHeight: 250 }}>
          <Typography
            variant="subtitle2"
            align="center"
            sx={{ pb: 0.5, mb: 1, borderBottom: 1, borderColor: 'divider' }}
          >
            Список категорий
          </Typography>
          <List dense disablePadding>
            {categories.map(({ name, id }) => (
              <ListItemButton
                key={id}
                selected={selectedCategory === id}
                onClick={() => handleCategoryClick(id)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemText
                  primary={name}
                  primaryTypographyProps={{ fontWeight: selectedCategory === id ? 700 : 400 }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <DndProvider backend={HTML5Backend}>
          <Paper variant="outlined" sx={{ p: 1.5, minHeight: 250 }}>
            <Droppable title="Вопросы в категории" items={columns.category} columnId="category" moveItem={moveItem} />
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, minHeight: 250 }}>
            <Droppable title="Список вопросов" items={columns.questions} columnId="questions" moveItem={moveItem} />
          </Paper>
        </DndProvider>
      </Box>

      <Box sx={{ textAlign: 'right' }}>
        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={<SaveRoundedIcon />}
          onClick={handleSubmit}
        >
          Сохранить
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
};
